import { useEffect } from 'react';
import { NavLink, Route, Routes, useLocation, useSearchParams } from 'react-router-dom';
import { cn } from '@vd/ui';
import { useAuth, useCurrentCompany } from '../../features/auth/AuthProvider';
import styles from './Settings.module.css';
import { GeneralCompanyTab } from './tabs/GeneralCompanyTab';
import { MembersTab } from './tabs/MembersTab';
import { TicketTypesTab } from './tabs/TicketTypesTab';
import { WidgetsOverviewTab } from './tabs/WidgetsOverviewTab';
import { BillingTab } from './tabs/BillingTab';
import { McpTokensTab } from './tabs/McpTokensTab';
import { CaptureProfilesTab } from './tabs/CaptureProfilesTab';
import { ArchivalTab } from './tabs/ArchivalTab';
import { AuditLogTab } from './tabs/AuditLogTab';
import { ExtensionSetupTab } from './tabs/ExtensionSetupTab';
import { McpSetupTab } from './tabs/McpSetupTab';
import { PreBuiltSkillTab } from './tabs/PreBuiltSkillTab';
import { AiTab } from './tabs/AiTab';
import { JiraTab } from './tabs/JiraTab';
import { useTabTitle, useTabWorkspaceName } from '../../lib/tabTitle';
import { WorkspaceMark } from '../../features/workspaces/WorkspaceMark';

// Absolute paths are required because the parent route
// (`/company/settings/*`) is a splat and we've opted into
// `v7_relativeSplatPath` (App.tsx). Under that flag, a relative
// `to="general"` from `/company/settings/members` would resolve to
// `/company/settings/members/general` (matching nothing) instead of
// replacing the splat segment.
const TAB_PREFIX = '/company/settings';
const tabs: Array<{ slug: string; label: string; ownerOnly?: boolean }> = [
  { slug: 'general', label: 'General' },
  { slug: 'members', label: 'Members' },
  { slug: 'ticket-types', label: 'Ticket types' },
  { slug: 'widgets', label: 'Bug report widget', ownerOnly: true },
  { slug: 'billing', label: 'Billing' },
  { slug: 'extension', label: 'Extension' },
  { slug: 'mcp-setup', label: 'MCP Connector' },
  { slug: 'pre-built-skill', label: 'Pre-built skill' },
  { slug: 'mcp-tokens', label: 'MCP tokens' },
  { slug: 'capture-profiles', label: 'Capture profiles' },
  { slug: 'archival', label: 'Archival' },
  { slug: 'ai', label: 'AI' },
  { slug: 'jira', label: 'Jira', ownerOnly: true },
  { slug: 'audit', label: 'Audit log' },
];

export function CompanySettingsPage() {
  // This tab's title (TOADO-905).
  const tabWorkspace = useTabWorkspaceName();
  useTabTitle({ parts: [tabWorkspace, "Settings"] });
  const current = useCurrentCompany();
  const isOwner = current?.role === 'owner';
  const visibleTabs = tabs.filter((t) => !t.ownerOnly || isOwner);
  const { companies, setCurrentCompany } = useAuth();
  // Wide comparisons and audit tables use the available settings content area.
  const pathname = useLocation().pathname;
  const billingPage = /\/billing\/?$/.test(pathname);
  const fullWidth = billingPage || /\/audit\/?$/.test(pathname);
  const [searchParams, setSearchParams] = useSearchParams();
  // A link from a project names its workspace (`?workspace=`): settings are the
  // selected workspace's, so it is selected before anything is shown, and a new
  // tab or a copied link opens that workspace's settings, never another's
  // (Codex review round 27). The site picker's own `workspace` comes with
  // `select=1` and is the Jira tab's. One this person is not in is ignored.
  const named = searchParams.has('select') ? null : searchParams.get('workspace');
  const namedCompany = named ? companies.find((company) => company.id === named) : undefined;
  useEffect(() => {
    if (named === null) return;
    if (namedCompany && current?.id !== namedCompany.id) {
      setCurrentCompany(namedCompany.id);
      return;
    }
    setSearchParams((params) => {
      params.delete('workspace');
      return params;
    }, { replace: true });
  }, [named, namedCompany, current?.id, setCurrentCompany, setSearchParams]);
  if (namedCompany && current?.id !== namedCompany.id) return null;
  return (
    <div className={cn(styles.page, fullWidth && styles.pageFull, billingPage && styles.billingPage)}>
      <aside className={styles.nav}>
        <div className={styles.navTitle} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {/* Which workspace these are, by its mark (TOADO-906). */}
          {current && <WorkspaceMark company={current} size={18} />}
          Company settings
        </div>
        {visibleTabs.map((t) => (
          <NavLink
            key={t.slug}
            to={`${TAB_PREFIX}/${t.slug}`}
            data-tour-anchor={`company-settings.${t.slug}`}
            className={({ isActive }: { isActive: boolean }) => cn(styles.navItem, isActive && styles.navActive)}
          >
            {t.label}
          </NavLink>
        ))}
      </aside>
      {/* What the workspace tour points at on the MCP, skill and extension pages (Jason, 28 Oct). */}
      <main className={styles.content} data-tour-anchor="company-settings.page">
        <Routes>
          <Route path="/" element={<GeneralCompanyTab />} />
          <Route path="general" element={<GeneralCompanyTab />} />
          <Route path="members" element={<MembersTab />} />
          <Route path="ticket-types" element={<TicketTypesTab />} />
          <Route path="widgets" element={<WidgetsOverviewTab />} />
          <Route path="billing" element={<BillingTab />} />
          <Route path="extension" element={<ExtensionSetupTab />} />
          <Route path="mcp-setup" element={<McpSetupTab />} />
          <Route path="pre-built-skill" element={<PreBuiltSkillTab />} />
          <Route path="mcp-tokens" element={<McpTokensTab />} />
          <Route path="capture-profiles" element={<CaptureProfilesTab />} />
          <Route path="archival" element={<ArchivalTab />} />
          <Route path="ai" element={<AiTab />} />
          <Route path="jira" element={<JiraTab />} />
          <Route path="audit" element={<AuditLogTab />} />
        </Routes>
      </main>
    </div>
  );
}
